<template>
  <tr
    class="clickable"
    :class="{ 'orange lighten-5': trainingLoad.latest_stats.injured }"
    @click="$emit('click', trainingLoad.id, $event)"
  >
    <td>
      <div class="d-flex align-center">
        <v-avatar
          size="32px"
          class="mr-2"
          color="grey lighten-2"
        >
          <v-img :src="trainingLoad.photo_url">
            <template v-slot:placeholder>
              <v-icon x-small>
                fal fa-user
              </v-icon>
            </template>
          </v-img>
        </v-avatar>
        <span>
          {{ trainingLoad.last_name }}, {{ trainingLoad.first_name }}
        </span>
        <v-spacer />
        <span v-if="trainingLoad.latest_stats.injured">
          <v-chip
            label
            small
            color="orange darken-2"
            dark
          >
            {{ $t('player_status.injured.capitalized') }}
          </v-chip>
        </span>
      </div>
    </td>
    <td
      v-if="$can('view-event-feedback')"
      style="width: 1%"
    >
      <div
        v-if="trainingLoad.event_stats.length === 0"
        class="grey--text"
      >
        N/A
      </div>
      <div v-else>
        <training-load-chip
          v-for="eventStat in trainingLoad.event_stats"
          :key="'event_stat_'+trainingLoad.id+eventStat.id"
          :training-load="eventStat.player_stats.TL"
          :training-grade="eventStat.player_stats.TG"
          :training-duration="eventStat.player_stats.TTM"
          :session-enjoyment="eventStat.player_stats.ENJY"
          :event-start-at="eventStat.start_at"
          :event-end-at="eventStat.end_at"
          :event-title="eventStat.title"
        />
      </div>
    </td>
    <td>
      <table-bar-value
        :value="trainingLoad.latest_stats.DL"
        :max="maxValues.DL"
        color="grey lighten-2"
      >
        {{ trainingLoad.latest_stats.DL }}
      </table-bar-value>
    </td>
    <td>
      <table-bar-value
        :value="trainingLoad.latest_stats.AL"
        :max="maxValues.AL"
        color="grey lighten-2"
      >
        {{ trainingLoad.latest_stats.AL }}
      </table-bar-value>
    </td>
    <td>
      <table-bar-value
        :value="trainingLoad.latest_stats.CL"
        :max="maxValues.CL"
        color="grey lighten-2"
      >
        {{ trainingLoad.latest_stats.CL }}
      </table-bar-value>
    </td>
    <td>
      <table-bar-value
        :value="trainingLoad.latest_stats.ACWR"
        :max="maxValues.ACWR"
        :color="getAcwrColorFromValue(trainingLoad.latest_stats.ACWR)"
      >
        {{ trainingLoad.latest_stats.ACWR }}
      </table-bar-value>
    </td>
    <td>
      <table-bar-value
        :value="trainingLoad.latest_stats.FRES"
        :max="maxValues.FRES"
        color="grey lighten-2"
      >
        {{ trainingLoad.latest_stats.FRES }}
      </table-bar-value>
    </td>
    <td v-if="showWellnessData">
      <table-selection-chip-value
        :value="trainingLoad.latest_stats.FAT"
        :max="5"
        reverse-colors
      />
    </td>
    <td
      v-if="showWellnessData"
      style="width: 0"
    >
      <table-selection-chip-value
        :value="trainingLoad.latest_stats.GMS"
        :max="5"
        reverse-colors
      />
    </td>
    <td
      v-if="showWellnessData"
      style="width: 0"
    >
      <table-selection-chip-value
        :value="trainingLoad.latest_stats.STL"
        :max="5"
        reverse-colors
      />
    </td>
    <td v-if="showWellnessData">
      <table-selection-chip-value
        :value="trainingLoad.latest_stats.MOO"
        :max="5"
      />
    </td>
    <td v-if="showWellnessData">
      <table-selection-chip-value
        :value="trainingLoad.latest_stats.SLQ"
        :max="5"
      />
    </td>
    <td v-if="showWellnessData">
      <table-bar-value
        :value="trainingLoad.latest_stats.SLD"
        :max="8"
        color="grey lighten-2"
      >
        {{ trainingLoad.latest_stats.SLD }}
      </table-bar-value>
    </td>
    <td>
      <pain-label
        :label="trainingLoad.latest_stats.highest_pain ? trainingLoad.latest_stats.highest_pain.label : undefined"
        :value="trainingLoad.latest_stats.highest_pain ? trainingLoad.latest_stats.highest_pain.value : undefined"
        :player-stats="trainingLoad.latest_stats"
        :gender="trainingLoad.gender"
      />
    </td>
    <td>
      <sickness-label :player-stats="trainingLoad.latest_stats" />
    </td>
    <td v-if="showMenstrualPeriod">
      <div
        v-if="trainingLoad.latest_stats.FMENSACT !== undefined"
      >
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-chip
              label
              outlined
              small
              :color="trainingLoad.latest_stats.FMENSACT ? 'blue' : 'grey'"
              v-on="on"
            >
              {{ trainingLoad.latest_stats.FMENSACT ? $t('status.yes') : $t('status.no') }}
              <span
                v-if="trainingLoad.latest_stats.FMENSFR"
                class="ml-1"
              >
                ({{ trainingLoad.latest_stats.FMENSFR }})
              </span>
            </v-chip>
          </template>
          <div>
            <div v-if="trainingLoad.latest_stats.FMENSFR">
              {{ $t('individual_analysis.menstrual_period.variable.flow_rate') }} <strong>{{ trainingLoad.latest_stats.FMENSFR }}</strong>
            </div>
            <div v-if="trainingLoad.latest_stats.last_menstrual_period_start">
              Last period started: <strong>{{ $dayjs(trainingLoad.latest_stats.last_menstrual_period_start).format('L') }}</strong>
            </div>
            <div v-if="trainingLoad.latest_stats.last_menstrual_period_end">
              Last period ended: <strong>{{ $dayjs(trainingLoad.latest_stats.last_menstrual_period_end).format('L') }}</strong>
            </div>
          </div>
        </v-tooltip>
      </div>
      <div
        v-else
        class="grey--text caption"
      >
        N/A
      </div>
    </td>
    <td v-if="showMenstrualPeriod">
      <menstrual-phases
        v-if="trainingLoad.latest_stats.cycle_phase && trainingLoad.latest_stats.cycle_phase !== noPhase"
        :current-menstrual-phase="trainingLoad.latest_stats.cycle_phase"
        :stats="trainingLoad.latest_stats"
      />
      <span
        v-else
        class="grey--text caption"
      >
        N/A
      </span>
    </td>
  </tr>
</template>

<script>
import TableBarValue from '@/components/helpers/TableBarValue'
import TableSelectionChipValue from '@/components/helpers/TableSelectionChipValue'
import TrainingLoadChip from '@/components/helpers/TrainingLoadChip'
import { acwrColorsDefinition } from '@/data/models/Stats'
import { getColorForValueWithDefinition } from '@/utils/helpers'
import { MenstrualPhase } from '@/data/models'
import MenstrualPhases from '@/components/helpers/MenstrualPhases.vue'
import PainLabel from '@/components/helpers/PainLabel.vue'
import SicknessLabel from '@/components/helpers/SicknessLabel.vue'

export default {
  name: 'InternalLoadTableRow',
  components: { SicknessLabel, PainLabel, MenstrualPhases, TrainingLoadChip, TableSelectionChipValue, TableBarValue },
  props: {
    trainingLoad: {
      type: Object,
      required: true
    },
    maxValues: {
      type: Object,
      required: true
    },
    showMenstrualPeriod: {
      type: Boolean,
      default: false
    },
    showWellnessData: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    }
  },
  created() {
    this.noPhase = MenstrualPhase.NO_PHASE
  },
  methods: {
    getAcwrColorFromValue(value) {
      return getColorForValueWithDefinition(value, acwrColorsDefinition)
    }
  }
}
</script>
